/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import JsonQuery from 'json-query';
import { merge } from 'object-path-immutable';
import parse from 'parse-json';
import { useContext } from 'react';
import {
    components as commonComponents,
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
    localization,
} from '@sitebuilder/common';
import {
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
    getThemeBasedColorPalette,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import { sampleData } from '../ChartTable/sampleData';
import BarChartStyles from './BarChart.styles';

if (typeof window !== `undefined`) {
    highchartsAccessibility(Highcharts);
}

export interface highChartOptions {
    path?: string;
    value?: string;
    valueType?: string;
    functionToTransform?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    dataKey: string;
    options: highChartOptions[];
    chartTitles: Headings[];
    paletteId?: string;
    typographyClass?: string;
    useManualBarWidthPadding?: boolean;
    limitYAxisRangeToData?: boolean;
    disableTabStaticData?: boolean;
    formatTo: string;
    showPlotLines?: boolean;
    plotLineColor?: string;
    plotLineWidth?: string;
    plotLineValue?: string;
}

interface CustomChartOptions extends Highcharts.Options {
    chart?: Highcharts.ChartOptions & {
        formatTo?: string;
    };
}

const BarChart = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType, colorPalettes, documentMetadata } = useContext(
        commonComponents.AppContext.AppContext
    );
    const { locale } = documentMetadata;
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        dataKey,
        chartTitles,
        options,
        paletteId,
        useManualBarWidthPadding,
        limitYAxisRangeToData,
        disableTabStaticData,
        formatTo,
        ...rest
    } = widgetProperties || {};
    const { data } = props;
    const i18n = commonHooks.useLocalization();

    // for mapping data
    const barChartData: any = dataKey ? JsonQuery(`${dataKey}`, { data }).value : sampleData;
    const sortedData = Array.isArray(barChartData)
        ? parse(JSON.stringify(barChartData)).sort((a: any, b: any) => a.sortOrder - b.sortOrder)
        : [];
    const processedData = sortedData?.map((a: any) => ({
        name: a.holding,
        y: a.allocation !== 0 && a.allocation !== null ? a.allocation * 100 : null,
    }));
    const processedDataLong = sortedData?.map((a: any) => ({
        name: a.holding,
        y: a.allocationLong !== 0 && a.allocationLong !== null ? a.allocationLong * 100 : null,
    }));
    const processedDataShort = sortedData?.map((a: any) => ({
        name: a.holding,
        y: a.allocationShort !== 0 && a.allocationShort !== null ? a.allocationShort * 100 : null,
    }));
    // Check if all series have any null data points to hide the Legend
    let anyAllNull = false;
    let allNull = true;
    let processedDataLongValues = false;
    let processedDataShortValues = false;
    processedDataLong?.forEach((series: any) => {
        if (series.y !== null && series.y !== 0) {
            allNull = false;
            processedDataLongValues = true;
        }
    });
    processedDataShort?.forEach((series: any) => {
        if (series.y !== null && series.y !== 0) {
            allNull = false;
            processedDataShortValues = true;
        }
    });

    if (allNull) {
        anyAllNull = true;
    }

    const showLegend = !anyAllNull;
    const barChartId = uuidv4();

    const orderedColors =
        data?.themeSelected && paletteId
            ? getThemeBasedColorPalette(colorPalettes?.[paletteId], data?.colorSpace)
            : getColorPalette();

    const appI18n = localization.getLocalization(locale);

    // Filter out null values from the data arrays
    const filteredDataLong = processedDataLong.filter((value: any) => !isNaN(value.y));
    const filteredDataShort = processedDataShort.filter((value: any) => !isNaN(value.y));
    const filteredData = processedData.filter((value: any) => !isNaN(value.y));

    // Calculate the range based on minimum and maximum values of the data
    const hasNegativeValues =
        filteredData.some((v: any) => v.y < 0) ||
        filteredDataShort.some((v: any) => v.y < 0) ||
        filteredDataLong.some((v: any) => v.y < 0);
    const maxAbsValueData = Math.max(
        ...filteredData.map((v: any) => {
            return Math.abs(v.y);
        })
    );

    const maxAbsValueDataLong = Math.max(
        ...filteredDataLong.map((v: any) => {
            return Math.abs(v.y);
        })
    );
    const maxAbsValueDataShort = Math.max(
        ...filteredDataShort.map((v: any) => {
            return Math.abs(v.y);
        })
    );
    let maximumY: any, minimumY: any;

    if (limitYAxisRangeToData) {
        const dataArrays = [filteredData, filteredDataLong, filteredDataShort];
        // Initialize variables to store the minimum and maximum y values
        let minY = Infinity;
        let maxY = -Infinity;

        // Iterate through each array in the dataArrays
        for (const dataArray of dataArrays) {
            // Iterate through each object in the array
            for (const obj of dataArray) {
                // Check if the object has a "y" key and the "y" value is not null
                if (obj?.y !== null) {
                    // Update the minimum and maximum y values
                    minY = Math.min(minY, obj.y);
                    maxY = Math.max(maxY, obj.y);
                }
            }
        }

        // Return an object containing both the minimum and maximum y values
        minimumY = minY;
        maximumY = maxY;
    }

    const maxAbsValue = Math.max(maxAbsValueData, maxAbsValueDataLong, maxAbsValueDataShort);
    const range = Math.ceil(maxAbsValue * 10) / 10;
    let yMin: any, yMax: any;
    if (range !== -Infinity) {
        yMin = hasNegativeValues ? (limitYAxisRangeToData ? minimumY : -range) : 0;
        yMax = limitYAxisRangeToData ? maximumY : range;
    }

    // Calculating height for Chart:
    const minHeight = processedData.length <= 6 ? 110 : 40;
    let chartHeight = minHeight + processedData.length * 30;
    if (processedDataLongValues && processedDataShortValues) {
        chartHeight = minHeight + processedData.length * 40;
    } else if (processedDataLongValues && processedDataShortValues) {
        chartHeight = minHeight + processedData.length * 35;
    }

    const pointWidth = anyAllNull ? 20 : 10;
    const groupPadding = anyAllNull ? 0 : 0.25;

    // Plot line options
    const plotLineWidth = isNaN(Number(rest?.plotLineWidth))
        ? 0
        : parseFloat(rest?.plotLineWidth || '');
    const plotLineColor = rest?.plotLineColor || '';
    const plotLineValue = isNaN(Number(rest?.plotLineValue))
        ? 0
        : parseFloat(rest?.plotLineValue || '');

    const getCommonOptions = (): CustomChartOptions => {
        return {
            accessibility: {
                enabled: true,
                keyboardNavigation: {
                    order: ['rangeSelector', 'zoom', 'legend', 'series', 'container', 'chartMenu'],
                },
            },
            chart: {
                type: 'bar',
                spacingTop: 25,
                spacingRight: 10,
                spacingLeft: 5,
                marginBottom: 75,
                height: chartHeight,
                formatTo: formatTo,
            },
            title: {
                text: '',
            },
            xAxis: {
                type: 'category',
                labels: {
                    style: {
                        fontSize: '16px',
                    },
                },
                lineWidth: 1,
                plotLines: processedData.map((_: any, index: number) => ({
                    color: plotLineColor,
                    width: plotLineWidth,
                    value: index + plotLineValue,
                })),
            },
            yAxis: {
                title: { text: null },
                lineWidth: 1,
                tickLength: 4,
                tickWidth: 1.5,
                tickAmount: 5,
                min: yMin,
                max: yMax,
                labels: {
                    format: '{value}%',
                },
            },
            legend: {
                symbolRadius: 0,
                reversed: true,
                verticalAlign: 'top',
                align: 'left',
                x: 0,
                y: 0,
                enabled: showLegend,
            },
            credits: { enabled: false },
            colors: orderedColors,
            plotOptions: {
                bar: {
                    animation: false,
                    grouping: true,
                    allowPointSelect: false,
                    enableMouseTracking: false,
                    minPointLength: 2,
                    dataLabels: {
                        enabled: !!formatTo,
                        formatter(this: any) {
                            if (formatTo) {
                                return data?.lang === 'fr'
                                    ? `${appI18n
                                          .t(this?.y, formatTo)
                                          .toString()
                                          .replace('.', ',')}%`
                                    : `${appI18n.t(this?.y, formatTo)}%`;
                            } else {
                                return `${this?.y}%`;
                            }
                        },
                    },
                },
            },
        };
    };
    let highChartOptions: Highcharts.Options = {
        ...getCommonOptions(),
        series: [
            {
                type: 'bar',
                name: appI18n.t('bar_chart_long_label'),
                data: processedDataLong,
                pointWidth: pointWidth,
                groupPadding: groupPadding,
            },
            {
                type: 'bar',
                name: appI18n.t('bar_chart_net_label'),
                data: processedData,
                pointWidth: pointWidth,
                groupPadding: groupPadding,
            },
            {
                type: 'bar',
                name: appI18n.t('bar_chart_short_label'),
                data: processedDataShort,
                pointWidth: pointWidth,
                groupPadding: groupPadding,
            },
        ],
    };

    // create new high chart options to remove point width and group padding
    let manualHighChartOptions: Highcharts.Options = {
        ...getCommonOptions(),
        series: [
            {
                type: 'bar',
                name: appI18n.t('bar_chart_long_label'),
                data: processedDataLong,
            },
            {
                type: 'bar',
                name: appI18n.t('bar_chart_net_label'),
                data: processedData,
            },
            {
                type: 'bar',
                name: appI18n.t('bar_chart_short_label'),
                data: processedDataShort,
            },
        ],
    };

    Array.isArray(options) &&
        options.forEach((o) => {
            let userOptions;

            try {
                if (!o.valueType || o.valueType === 'jsonString') {
                    userOptions = parse((o.value as string) || '{}');
                } else {
                    const transformFunctionName = `${props.widgetInstanceId}-option-${o.path}-transform`;
                    userOptions = commonUtils.transformDataWithGlobals(
                        transformFunctionName,
                        o.functionToTransform,
                        { data, i18n }
                    );
                }
            } catch (error) {
                userOptions = {};
            }

            highChartOptions = merge(highChartOptions, o.path, userOptions);
            manualHighChartOptions = merge(manualHighChartOptions, o.path, userOptions);
        });

    return (
        <>
            <div
                className={`bar-chart ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${BarChartStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
            >
                {processedData.length > 0 && (
                    <>
                        <HeaderWrapper
                            headings={chartTitles}
                            data={data}
                            disableTabStaticData={
                                globalDisableTabStaticData || disableTabStaticData
                            }
                        />
                        <div id={`${barChartId}`} className="js-bar-chart-tile">
                            {['pdf', 'pptx'].includes(documentType) ? (
                                <div id={`${barChartId}-bar-chart`} />
                            ) : useManualBarWidthPadding ? (
                                <HighchartsReact
                                    key={'manual'}
                                    highcharts={Highcharts}
                                    options={manualHighChartOptions}
                                />
                            ) : (
                                <HighchartsReact
                                    key={'default'}
                                    highcharts={Highcharts}
                                    options={highChartOptions}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
            {['pdf', 'pptx'].includes(documentType) && processedData.length > 0 && (
                <>
                    <script
                        id={`${barChartId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(
                                useManualBarWidthPadding ? manualHighChartOptions : highChartOptions
                            ),
                        }}
                    />
                    <script
                        id={`${barChartId}-locale`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(locale),
                        }}
                    ></script>
                    <script
                        id={`${barChartId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify([
                                {
                                    type: 'bar',
                                    name: appI18n.t('bar_chart_long_label'),
                                    data: processedDataLong,
                                    ...(!useManualBarWidthPadding && {
                                        pointWidth: pointWidth,
                                        groupPadding: groupPadding,
                                    }),
                                },
                                {
                                    type: 'bar',
                                    name: appI18n.t('bar_chart_net_label'),
                                    data: processedData,
                                    ...(!useManualBarWidthPadding && {
                                        pointWidth: pointWidth,
                                        groupPadding: groupPadding,
                                    }),
                                },
                                {
                                    type: 'bar',
                                    name: appI18n.t('bar_chart_short_label'),
                                    data: processedDataShort,
                                    ...(!useManualBarWidthPadding && {
                                        pointWidth: pointWidth,
                                        groupPadding: groupPadding,
                                    }),
                                },
                            ]),
                        }}
                    />
                </>
            )}
        </>
    );
};

export default BarChart;
